import { usePopUpMessage } from '@hb/react'
import { AnyObject, arrayToObject, BaseQuery, UseQuery, WithId } from '@hb/shared'
import { onSnapshot, Query } from 'firebase/firestore'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { db } from '../../backend/db/db'
import { toFirestoreQuery } from '../../backend/db/utils/queries'

export const useQuery = <T extends AnyObject>(
  ref: Query<T> | null,
  onNewData?: (newData: Record<string, T> | null) => void,
): UseQuery<T> => {
  const { showError } = usePopUpMessage()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dataArr, setDataArr] = useState<WithId<T>[] | null>(null)
  const data = useMemo(() => (dataArr ? (arrayToObject(dataArr) ?? {}) : null), [dataArr])
  // const [data, setData] = useState<Record<string, T> | null>(null)
  const onError = useCallback(
    (message: string) => {
      console.error(message)
      setError(message)
      showError(message)
    },
    [showError],
  )

  useEffect(() => {
    if (!ref) {
      setDataArr(null)
      return () => {}
    }
    setLoading(true)
    return onSnapshot(
      ref,
      res => {
        setLoading(false)
        if (res.empty) {
          setDataArr([])
        } else {
          // const updated = res.docs.reduce<Record<string, T>>(
          //   (acc, curr) => ({
          //     ...acc,
          //     [curr.id]: curr.data(),
          //   }),
          //   {},
          // )
          const updated = res.docs.map(doc => ({ ...doc.data(), id: doc.id }))
          if (onNewData) onNewData(arrayToObject(updated) ?? null)
          setDataArr(updated)
        }
        setError(null)
      },
      err => {
        setLoading(false)
        console.error(err)
        console.error(ref)
        switch (err.code) {
          case 'permission-denied':
            onError('Insufficient permissions')
            break
          case 'unauthenticated':
            onError('Not authenticated')
            break
          default:
            onError('Error getting firestore document')
            break
        }
      },
    )
  }, [ref, onError, onNewData])

  return {
    dataArr,
    data,
    loading,
    error,
  }
}

export const useBaseQuery = <T extends AnyObject>(baseQuery: BaseQuery<T> | null): UseQuery<T> => {
  const firebaseQuery = useMemo(
    () => (baseQuery ? toFirestoreQuery(db, baseQuery) : null),
    [baseQuery],
  )

  return useQuery(firebaseQuery)
}
